// Central auth state: current user, token, login/logout.
// Keep this lean — token storage + user identity only.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi } from '../api/auth';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const TOKEN_KEY = 'access_token';
const EXPIRES_KEY = 'token_expires_at';

function readToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
}

function writeToken(token, expiresAt, remember) {
  clearToken();
  const store = remember ? localStorage : sessionStorage;
  store.setItem(TOKEN_KEY, token);
  if (expiresAt) store.setItem(EXPIRES_KEY, expiresAt);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRES_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(EXPIRES_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  const loadCurrentUser = useCallback(async () => {
    if (!readToken()) {
      setUser(null);
      setIsChecking(false);
      return;
    }
    try {
      const me = await authApi.me();
      setUser(me);
    } catch (err) {
      clearToken();
      setUser(null);
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  const login = useCallback(async (email, password, remember) => {
    const res = await authApi.login(email, password, !!remember);
    writeToken(res.access_token, res.expires_at, !!remember);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (_) {
      // non-fatal — token is discarded locally regardless
    }
    clearToken();
    setUser(null);
    toast.success('Logged out');
  }, []);

  const hasRole = useCallback(
    (...roles) => !!user && roles.includes(user.role),
    [user]
  );

  const value = {
    user,
    isAuthenticated: !!user,
    isChecking,
    login,
    logout,
    hasRole,
    refresh: loadCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
